import camelize from 'camelize';
import decamelizeKeys from 'decamelize-keys';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import moment from 'moment';
import QueryString from 'query-string';
import { EventTypes } from 'redux-segment';

import { MeetingActions, SegmentActions } from 'actions/actionTypes';

import { errorNotification } from 'components/ToastNotifications';

import L10n from 'helpers/L10n';
import env from 'helpers/env';
import { deconstructFiltersFromUrl } from 'helpers/filters';
import { deleteMethod, get, patch, post } from 'helpers/http';
import { L10Errors } from 'helpers/l10n/L10nErrors';
import { getMediaType } from 'helpers/media';

import { FILTERS_PARAMS_OMITTED_FROM_CALENDAR_VIEW_MEETINGS } from 'constants/filters';
import { PAGE_SIZE, PAST_MEETINGS_PAGE_SIZE } from 'constants/meetings';

const DATE_FILTERS = new Set([
  'start_at__lte',
  'start_at__gte',
  'end_at__lte',
  'end_at__gte'
]);
const DB_SUPPORTED_FILTERS = new Set([
  ...DATE_FILTERS,
  'include_states',
  'recording_duration__isnull',
  'recording_duration__gte',
  'recording_duration__lte',
  'is_call',
  'is_internal'
]);

const getQuerySkipTimeRelatedQueries = query =>
  Object.keys(query ?? {}).reduce((previous, current) => {
    if (
      // all the query params use current date which changes on every request
      // so skiping them as they change always
      ['end_at__lte', 'start_at__lte', 'end_at__gte'].includes(current)
    ) {
      return previous;
    }
    previous[current] = query[current];
    return previous;
  }, {});

const getUpcomingQueryParams = url => {
  try {
    const queryString = new URL(url);
    const query = deconstructFiltersFromUrl(queryString.search.slice(1));
    return getQuerySkipTimeRelatedQueries(query);
  } catch {
    return {};
  }
};

const reportMeetingFetchError = ({
  error,
  url,
  component,
  method,
  action = 'fetch meetings'
}) => {
  errorNotification({
    message: L10n.meetingsViews.noResultsError,
    error,
    component,
    action,
    api: {
      method,
      endpoint: url,
      url,
      status: error?.status,
      errorCode: error?.name
    }
  });
};

export const fetchAllMeetingsV3 =
  ({
    type = 'allMeetings',
    time,
    payload,
    fetchType = 'filter_meetings',
    forceRefetch,
    // Tags the request so the backend can apply context-specific defaults/limits (e.g. the agent
    // test-run preview) — empty string (rather than omitting the default) so callers that don't
    // pass it stay optional under TS's structural inference for this destructured parameter.
    ruleContext = '',
    ...rest
  }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();

    const { meetings = {} } = getState();
    const currentMeetingType = meetings[type] ?? {};
    const currentMeetings = currentMeetingType[time] ?? {};
    const value = requestTs - currentMeetings.requestTs;
    const cachedPayload = currentMeetings?.payload;

    let isFilterChanged = false;
    let avoidFetching = false;

    avoidFetching = isEqualWith(payload, cachedPayload, (val1, val2) => {
      if (
        val1?.filter_groups &&
        val2?.filter_groups &&
        !isEqual(val1.filter_groups, val2.filter_groups)
      ) {
        return false;
      }

      if (
        (val1?.time_range &&
          val2?.time_range &&
          isEqual(val1.time_range, val2.time_range)) ||
        (val1?.view_type &&
          val2?.view_type &&
          isEqual(val1.view_type, val2.view_type))
      ) {
        return true;
      }

      // Custom comparison logic for meeting_end_at__lte
      if (
        val1?.meeting_timeframe?.meeting_end_at__lte &&
        val2?.meeting_timeframe?.meeting_end_at__lte
      ) {
        const diff = moment(val1.meeting_timeframe.meeting_end_at__lte).diff(
          moment(val2.meeting_timeframe.meeting_end_at__lte)
        );

        const diffInMins = moment.duration(diff).asMinutes();
        /* Fetch meetings if only End Date has been changed by the user */
        if (diffInMins > 0 && diffInMins < 5) {
          return true;
        }
      }
    });

    if (payload && cachedPayload && !avoidFetching) {
      isFilterChanged = true;
    }

    if (value < 300000 && avoidFetching && !forceRefetch) {
      return;
    }

    const controller = new AbortController();
    const url = `${
      env.avomaApiUrl
    }/api/v3/meetings/${fetchType}/?page_size=10&page=1&skip_count=true&skip_attendee_details=true${
      ruleContext ? `&rule_context=${ruleContext}` : ''
    }`;

    await dispatch({
      type: 'FETCH_MEETINGS_REQUEST',
      category: type,
      time,
      requestTs,
      payload,
      isFilterChanged,
      url,
      controller
    });

    try {
      const data = await post(url, {
        ...payload,
        ...rest
      });

      dispatch({
        type: 'FETCH_MEETINGS_SUCCESS',
        category: type,
        time,
        requestTs,
        payload,
        data,
        forceRefetch
      });
    } catch (e) {
      dispatch({
        type: 'FETCH_MEETINGS_FAILURE',
        category: type,
        time,
        requestTs,
        payload,
        error: e.message
      });

      reportMeetingFetchError({
        error: e,
        url,
        component: 'MeetingsListingWrapper',
        method: 'POST'
      });
    }
  };

export const fetchNextMeetingsPage =
  ({ payload, time, url, categoryType, ...rest }) =>
  dispatch => {
    if (!url) {
      throw Error('[avoma] requested next page without specifying a url');
    }

    const requestTs = Date.now();
    const category = categoryType;

    dispatch({
      type: 'FETCH_NEXT_MEETINGS_REQUEST',
      category,
      requestTs,
      time,
      payload
    });

    post(url, { ...payload, ...rest })
      .then(page => {
        dispatch({
          type: 'FETCH_NEXT_MEETINGS_SUCCESS',
          data: page,
          category,
          requestTs,
          time,
          payload
        });
      })
      .catch(err => {
        dispatch({
          type: 'FETCH_NEXT_MEETINGS_FAILURE',
          category,
          requestTs,
          time,
          payload,
          error: err.message
        });
        throw err;
      });
  };

// Used only for the desktop app zen mode. For everything else, refer to the V3 meeting api and functions
export const fetchUserMeetingsV2 =
  ({ upcoming, fullUrl = '', user, forceRefetch = false }) =>
  async (dispatch, getState) => {
    let url;
    const time = upcoming ? 'upcoming' : 'past';
    if (!fullUrl) {
      const endTimeParam = upcoming ? 'end_at__gte' : 'end_at__lte';
      const startTimeParam = upcoming ? null : 'start_at__lte';
      const order = upcoming ? 'start_at' : '-start_at';

      const currDate = new Date();
      const todayDate = moment(currDate).startOf('day').toDate();

      const pageSize = upcoming ? PAGE_SIZE : PAST_MEETINGS_PAGE_SIZE;

      const startTimeQuery = startTimeParam
        ? `${startTimeParam}=${currDate.toISOString()}`
        : '';

      const endTimeQuery = `${endTimeParam}=${
        upcoming ? todayDate.toISOString() : currDate.toISOString()
      }`;

      url = `${env.avomaApiUrl}/api/v2/meetings/all/?user_email=${user}&o=${order}&page_size=${pageSize}&${endTimeQuery}&${startTimeQuery}`;
    } else {
      url = fullUrl;
    }
    await getMeetings({
      dispatch,
      url,
      type: 'myMeetings',
      time,
      getState,
      checkTime: true,
      forceRefetch
    });
  };

// This function is used to fetch the user's meetings for the desktop app notifications system
// This code is a temporary solution until the notifications system is fully migrated to a backend triggered system in the future
export const fetchUserMeetingsV2ForNotifications = async ({ user }) => {
  const todayDate = moment();
  const endTimeQuery = `end_at__gte=${todayDate.toISOString()}`;

  const url = `${env.avomaApiUrl}/api/v2/meetings/all/?user_email=${user}&o=start_at&page_size=5&${endTimeQuery}`;

  const data = await get(url);

  data.results = await Promise.all(
    data.results.map(async meeting => {
      const conferenceData = await get(
        `${env.avomaApiUrl}/api/v2/meetings/${meeting.uuid}/conference_data/`
      );
      return { ...meeting, ...conferenceData };
    })
  );

  return camelize(data);
};

// Used only for the desktop app zen mode. For everything else, refer to the V3 meeting api and functions
export const fetchNextMeetingsPageForV2Api =
  ({ payload, time, url, categoryType, ...rest }) =>
  dispatch => {
    if (!url) {
      throw Error('[avoma] requested next page without specifying a url');
    }

    const requestTs = Date.now();
    const category = categoryType;

    dispatch({
      type: 'FETCH_NEXT_MEETINGS_REQUEST',
      category,
      requestTs,
      time,
      payload
    });

    get(url, { ...payload, ...rest })
      .then(page => {
        dispatch({
          type: 'FETCH_NEXT_MEETINGS_SUCCESS',
          data: page,
          category,
          requestTs,
          time,
          payload
        });
      })
      .catch(err => {
        dispatch({
          type: 'FETCH_NEXT_MEETINGS_FAILURE',
          category,
          requestTs,
          time,
          payload,
          error: err.message
        });
        throw err;
      });
  };

const getMeetings = async ({
  dispatch,
  url: getUrl,
  type,
  time,
  getState,
  checkTime = false,
  query,
  forceRefetch = false
}) => {
  const requestTs = Date.now();
  const controller = new AbortController();
  let isQueryChanged = false;
  if (checkTime) {
    const { meetings = {} } = getState();
    const currentMeetingType = meetings[type] ?? {};
    const currentMeetings = currentMeetingType[time] ?? {};
    const value = requestTs - currentMeetings.requestTs;
    let avoidFetching = false;
    if (time === 'past') {
      const queryTemp = getQuerySkipTimeRelatedQueries(query);
      const reduxQueryTemp = getQuerySkipTimeRelatedQueries(
        currentMeetings?.query ?? {}
      );
      avoidFetching = isEqual(queryTemp, reduxQueryTemp);
      if (!isEqual(queryTemp, reduxQueryTemp)) {
        isQueryChanged = true;
      }
    } else if (time === 'upcoming') {
      const queryTemp = getUpcomingQueryParams(getUrl);
      const reduxQueryTemp = getUpcomingQueryParams(currentMeetings?.url);
      avoidFetching = isEqual(queryTemp, reduxQueryTemp);
    }

    // Data is not stale within 5 mins
    // if the query is same and above condition avoid calls
    if (value < 300000 && avoidFetching && !forceRefetch) {
      return;
    }
  }
  dispatch({
    type: 'FETCH_MEETINGS_REQUEST',
    category: type,
    time,
    requestTs,
    query,
    isQueryChanged,
    url: getUrl,
    forceRefetch,
    controller
  });
  try {
    const data = await get(getUrl, {
      signal: controller.signal
    });
    dispatch({
      type: 'FETCH_MEETINGS_SUCCESS',
      category: type,
      time,
      data,
      requestTs,
      forceRefetch
    });
  } catch (e) {
    dispatch({
      type: 'FETCH_MEETINGS_FAILURE',
      category: type,
      time,
      error: e.message,
      requestTs,
      forceRefetch
    });
  }
};

export const fetchCurrentlySelectedMeeting = meetingId => async dispatch => {
  if (!meetingId) {
    throw Error('[avoma] requested next page without specifying a meetingId');
  }

  dispatch({ type: 'FETCH_A_MEETING_REQUEST' });
  try {
    const meeting = await get(
      `${env.avomaApiUrl}/api/v1/meetings/${meetingId}/`
    );
    if (meeting.uuid) {
      const camelizedMeeting = camelize(meeting);
      dispatch({
        type: 'FETCH_A_MEETING_SUCCESS',
        meeting: camelizedMeeting
      });
      return camelizedMeeting;
    }
    dispatch({
      type: 'FETCH_A_MEETING_FAILURE',
      error: 'Unable to fetch meeting'
    });
    return null;
  } catch (err) {
    dispatch({
      type: 'FETCH_A_MEETING_FAILURE',
      error: err.name,
      errorDetail: err.message
    });
    return null;
  }
};

// V2 zen-mode list + conference_data omit the hyperlinked `conference` URL that
// setBotState needs. Resolve it from the store or v1 meeting detail on demand.
// Merges conference via MERGE_MEETING_CONFERENCE (not FETCH_A_MEETING_*) so we
// never move fetchingAMeeting and don't clobber list botState with a full v1 payload.
export const fetchMeetingConferenceUrl =
  meetingUuid => async (dispatch, getState) => {
    if (!meetingUuid) {
      return null;
    }

    const cachedConference = getState().meetings.data[meetingUuid]?.conference;
    if (cachedConference) {
      return cachedConference;
    }

    try {
      const meeting = await get(
        `${env.avomaApiUrl}/api/v1/meetings/${meetingUuid}/`
      );
      const camelizedMeeting = camelize(meeting);
      if (camelizedMeeting.uuid && camelizedMeeting.conference) {
        dispatch({
          type: 'MERGE_MEETING_CONFERENCE',
          meeting: {
            uuid: camelizedMeeting.uuid,
            conference: camelizedMeeting.conference
          }
        });
      }
      return camelizedMeeting.conference || null;
    } catch {
      return null;
    }
  };

export const fetchMeetingMeta = meetingId => async dispatch => {
  dispatch({ type: MeetingActions.FETCH_META_REQUEST });
  try {
    const meetingMeta = await get(
      `${env.avomaApiUrl}/api/v1/meetings/${meetingId}/meta/`
    );
    dispatch({
      type: MeetingActions.FETCH_META_SUCCESS,
      meetingId,
      metadata: camelize(meetingMeta)
    });
  } catch (err) {
    dispatch({ type: MeetingActions.FETCH_META_FAILURE });
  }
};

export const setBotState =
  ({ conference, uuid, state, recurring = false }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.SET_BOT_STATE,
      meta: {
        analytics: {
          eventType: EventTypes.track,
          eventPayload: {
            properties: {
              state
            }
          }
        }
      }
    });

    try {
      const response = await post(`${conference}change_state/`, {
        state,
        update_future_events_in_series: recurring
      });
      dispatch({
        type: MeetingActions.SET_BOT_STATE_SUCCESS,
        uuid,
        conference: response
      });
    } catch (err) {
      dispatch({
        type: MeetingActions.SET_BOT_STATE_FAILURE,
        error: err.message
      });
      throw err;
    }
  };

export const deleteMeetingSegment =
  (meetingId, segmentName, range) => async (dispatch, getState) => {
    const meeting = getState().meetings.data[meetingId];
    if (!meeting) {
      return;
    }
    const original = { ...meeting.segmentLabels };
    const payload = { ...meeting.segmentLabels };
    const ranges = [];
    for (let i = 0; i < payload[segmentName].length; i += 1) {
      const target = payload[segmentName][i];
      if (target[0] !== range[0] || target[1] !== range[1]) {
        ranges.push(target);
      }
    }
    payload[segmentName] = ranges;
    dispatch({
      type: SegmentActions.update.request,
      meetingId,
      segments: payload
    });
    try {
      const segments = await patch(
        `${env.avomaApiUrl}/api/v1/segments/${meetingId}/`,
        {
          segments: JSON.stringify(decamelizeKeys(payload)),
          uuid: meetingId
        }
      );
      dispatch({
        type: SegmentActions.update.success,
        meetingId,
        segments: camelize(segments)
      });
    } catch (err) {
      dispatch({
        type: SegmentActions.update.failure,
        meetingId,
        segments: original
      });
    }
  };

export const dialIntoMeeting = meetingId => async dispatch => {
  dispatch({ type: 'MEETING_DIALING_IN', uuid: meetingId });
  const result = await post(
    `${env.avomaApiUrl}/api/v1/ondemandmeetings/${meetingId}/join_now/`
  );
  const camelizedResult = camelize(result);
  dispatch({
    type: 'MEETING_DIALED_IN',
    uuid: meetingId,
    botState: camelizedResult.botState
  });
  return camelizedResult;
};

export const enableZoomCloudRecording = meetingUuid => async dispatch => {
  dispatch({
    type: MeetingActions.ENABLE_ZOOM_CLOUD_REC_REQUEST,
    uuid: meetingUuid
  });
  try {
    const meeting = await post(
      `${env.avomaApiUrl}/api/v1/zoom/meetings/${meetingUuid}/enable_cloud_rec/`
    );
    dispatch({
      type: MeetingActions.ENABLE_ZOOM_CLOUD_REC_SUCCESS,
      meeting: camelize(meeting)
    });
  } catch (err) {
    dispatch({
      type: MeetingActions.ENABLE_ZOOM_CLOUD_REC_ERROR,
      uuid: meetingUuid,
      error: err.message
    });
  }
};

export const disableZoomCloudRecording = meetingUuid => async dispatch => {
  dispatch({
    type: MeetingActions.DISABLE_ZOOM_CLOUD_REC_REQUEST,
    uuid: meetingUuid
  });
  try {
    const meeting = await post(
      `${env.avomaApiUrl}/api/v1/zoom/meetings/${meetingUuid}/disable_cloud_rec/`
    );
    dispatch({
      type: MeetingActions.DISABLE_ZOOM_CLOUD_REC_SUCCESS,
      meeting: camelize(meeting)
    });
  } catch (err) {
    dispatch({
      type: MeetingActions.DISABLE_ZOOM_CLOUD_REC_ERROR,
      uuid: meetingUuid,
      error: err.message
    });
  }
};

export const enableMsCloudRecording = meetingUuid => async dispatch => {
  dispatch({
    type: MeetingActions.ENABLE_MS_CLOUD_REC_REQUEST,
    uuid: meetingUuid
  });
  try {
    const meeting = await post(
      `${env.avomaApiUrl}/api/v1/microsoft/meetings/${meetingUuid}/enable_cloud_rec/`
    );
    dispatch({
      type: MeetingActions.ENABLE_MS_CLOUD_REC_SUCCESS,
      meeting: camelize(meeting)
    });
  } catch (err) {
    dispatch({
      type: MeetingActions.ENABLE_MS_CLOUD_REC_ERROR,
      uuid: meetingUuid,
      error: err.message
    });
  }
};

export const disableMsCloudRecording = meetingUuid => async dispatch => {
  dispatch({
    type: MeetingActions.DISABLE_MS_CLOUD_REC_REQUEST,
    uuid: meetingUuid
  });
  try {
    const meeting = await post(
      `${env.avomaApiUrl}/api/v1/microsoft/meetings/${meetingUuid}/disable_cloud_rec/`
    );
    dispatch({
      type: MeetingActions.DISABLE_MS_CLOUD_REC_SUCCESS,
      meeting: camelize(meeting)
    });
  } catch (err) {
    dispatch({
      type: MeetingActions.DISABLE_MS_CLOUD_REC_ERROR,
      uuid: meetingUuid,
      error: err.message
    });
  }
};

export const createImpromptu = details => async dispatch => {
  dispatch({ type: MeetingActions.IMPROMPTU_MEETING_REQUEST });
  try {
    let response = await post(
      `${env.avomaApiUrl}/api/v1/ondemandmeetings/impromptu/`,
      details
    );
    response = camelize(response);
    dispatch({
      type: MeetingActions.IMPROMPTU_MEETING_SUCCESS,
      meeting: response
    });
    return response;
  } catch (err) {
    dispatch({
      type: MeetingActions.IMPROMPTU_MEETING_FAILURE
    });
    throw err;
  }
};

export const updateConferenceInfo = async (url, details) => {
  const response = await patch(`${url}update_info/`, details);
  return camelize(response);
};

export const dropMeetingBot = meetingId => dispatch => {
  dispatch({ type: 'MEETING_BOT_DROPPING', uuid: meetingId });
  post(
    `${env.avomaApiUrl}/api/v1/ondemandmeetings/${meetingId}/drop_now/`
  ).then(data => {
    const meeting = camelize(data);
    dispatch({
      type: 'MEETING_BOT_DROPPED',
      uuid: meetingId,
      botState: meeting.botState,
      meetingStates: meeting.states
    });
  });
};

export const pauseMeetingBot = (meetingId, data) => dispatch => {
  const meeting = camelize(data);
  dispatch({
    type: 'MEETING_BOT_PAUSED',
    uuid: meetingId,
    botState: meeting.botState,
    meetingStates: meeting.states
  });
};

export const resumeMeetingBot = (meetingId, data) => dispatch => {
  const meeting = camelize(data);
  dispatch({
    type: 'MEETING_BOT_RESUMED',
    uuid: meetingId,
    botState: meeting.botState,
    meetingStates: meeting.states
  });
};

export const updateMeetingSubject =
  ({ meetingUuid, subject }) =>
  async dispatch => {
    const result = await patch(
      `${env.avomaApiUrl}/api/v1/meetingupdate/${meetingUuid}/update_subject/`,
      { subject }
    );
    const camelizedResult = camelize(result);
    // TODO: this result doesn't return the proper subject yet
    dispatch({
      type: MeetingActions.MEETING_SUBJECT_UPDATED,
      meetingUuid,
      subject
    });
    return camelizedResult;
  };

export const lookupMeetings = uuids => async dispatch => {
  dispatch({ type: MeetingActions.LOOKUP_MEETINGS_REQUEST });
  const uuidsList = uuids.map(uuid => `id=${uuid}`).join('&');
  try {
    const data = await get(
      `${env.avomaApiUrl}/api/v1/meetings/by_ids/?${uuidsList}`
    );
    dispatch({
      type: MeetingActions.LOOKUP_MEETINGS_SUCCESS,
      requestedUuids: uuids,
      meetings: data
    });
  } catch (err) {
    dispatch({ type: MeetingActions.LOOKUP_MEETINGS_FAILURE });
  }
};

export const deleteMeeting =
  ({ meetingUuid }) =>
  async dispatch => {
    dispatch({ type: MeetingActions.DELETE_MEETING_REQUEST });
    try {
      const deletedMeeting = await deleteMethod(
        `${env.avomaApiUrl}/api/v1/meetings/${meetingUuid}/`
      );
      dispatch({
        type: MeetingActions.DELETE_MEETING_SUCCESS,
        meetingUuid
      });
      return deletedMeeting;
    } catch (err) {
      dispatch({ type: MeetingActions.DELETE_MEETING_FAILURE });
      throw err;
    }
  };

// Updates recurring meetings locally
export const updateLocalMeetings =
  ({ query, value }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.UPDATE_LOCAL_MEETINGS,
      query,
      value
    });
  };

export const setRefetchNeeded = refetchNeeded => async dispatch => {
  dispatch({
    type: MeetingActions.SET_REFETCH_NEEDED,
    refetchNeeded
  });
};

export const createMeetingParticipants =
  ({ meetingUuid, participants }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.CREATE_PARTICIPANTS_REQUEST,
      meetingUuid,
      meta: {
        analytics: {
          eventType: EventTypes.track
        }
      }
    });
    try {
      const result = await post(
        `${env.avomaApiUrl}/api/v1/meetings/${meetingUuid}/participants/`,
        participants
      );
      dispatch({
        type: MeetingActions.CREATE_PARTICIPANTS_SUCCESS,
        meetingUuid,
        response: camelize(result)
      });
      return result;
    } catch (err) {
      dispatch({
        type: MeetingActions.CREATE_PARTICIPANTS_FAILURE,
        meetingUuid,
        error: err.message
      });
      throw err;
    }
  };

export const deleteMeetingParticipant =
  ({ meetingUuid, participantUuid }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.DELETE_PARTICIPANTS_REQUEST,
      meetingUuid,
      participantUuid,
      meta: {
        analytics: {
          eventType: EventTypes.track
        }
      }
    });
    try {
      const result = await deleteMethod(
        `${env.avomaApiUrl}/api/v1/meetings/${meetingUuid}/participants/${participantUuid}/`
      );
      dispatch({
        type: MeetingActions.DELETE_PARTICIPANTS_SUCCESS,
        meetingUuid,
        participantUuid
      });
      return result;
    } catch (err) {
      dispatch({
        type: MeetingActions.DELETE_PARTICIPANTS_FAILURE,
        meetingUuid,
        error: err.message
      });
      throw err;
    }
  };

export const fetchMeetingParticipants =
  ({ meetingUuid, forceRefetch = false }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();
    const shouldFetch = isStoreValueStale({
      getState,
      meetingUuid,
      entityType: 'attendees',
      forceRefetch
    });

    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_PARTICIPANTS_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const result = await get(
        `${env.avomaApiUrl}/api/v1/meetings/${meetingUuid}/participants/by_org/`
      );
      dispatch({
        type: MeetingActions.FETCH_PARTICIPANTS_SUCCESS,
        meetingUuid,
        response: camelize(result),
        requestTs
      });
      return result;
    } catch (err) {
      dispatch({
        type: MeetingActions.FETCH_PARTICIPANTS_FAILURE,
        meetingUuid,
        error: err.message,
        requestTs
      });
    }
  };

export const downloadMeeting =
  ({ meetingUuid, audio, video, transcript, transcriptVtt, transcriptTxt }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.DOWNLOAD_MEETING_REQUEST,
      meetingUuid,
      meta: {
        analytics: {
          eventType: EventTypes.track
        }
      }
    });
    const body = {
      meeting_uuids: [meetingUuid],
      audio,
      video,
      transcript,
      transcript_vtt: transcriptVtt,
      transcript_txt: transcriptTxt
    };
    try {
      const response = await post(
        `${env.avomaApiUrl}/api/v1/download/`,
        omitBy(body, isNil)
      );
      const result = camelize(response[0]);
      dispatch({
        type: MeetingActions.DOWNLOAD_MEETING_SUCCESS,
        meetingUuid,
        response: result
      });
      return result;
    } catch (err) {
      dispatch({
        type: MeetingActions.DOWNLOAD_MEETING_FAILURE,
        meetingUuid,
        error: err.message
      });
      throw err;
    }
  };

// show meeting/snippet/snippets share modal
export const showSharingModal = payload => dispatch => {
  dispatch({
    type: MeetingActions.SHOW_SHARING_MODAL,
    response: payload,
    meta: {
      analytics: EventTypes.track
    }
  });
};

// hide share modal
export const hideSharingModal = () => dispatch => {
  dispatch({
    type: MeetingActions.HIDE_SHARING_MODAL,
    meta: {
      analytics: EventTypes.track
    }
  });
};

// show create snippet modal
export const showCreateSnippetModal = payload => dispatch => {
  dispatch({
    type: MeetingActions.SHOW_CREATE_SNIPPET_MODAL,
    response: payload,
    meta: {
      analytics: EventTypes.track
    }
  });
};

// hide create snippet modal
export const hideCreateSnippetModal = () => dispatch => {
  dispatch({
    type: MeetingActions.HIDE_CREATE_SNIPPET_MODAL,
    meta: {
      analytics: EventTypes.track
    }
  });
};

// meeting privacy update api call
export const updateMeetingPrivacy =
  ({ meetingUuid, payload }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.UPDATE_MEETING_PRIVACY_REQUEST
    });
    try {
      const result = await post(
        `${env.avomaApiUrl}/api/v1/meetings/${meetingUuid}/change_privacy/`,
        payload
      );
      const response = camelize(result);
      dispatch({
        type: MeetingActions.UPDATE_MEETING_PRIVACY_SUCCESS,
        meetingUuid,
        response
      });
      return { response, error: null };
    } catch (err) {
      dispatch({
        type: MeetingActions.UPDATE_MEETING_PRIVACY_ERROR,
        error: err.message
      });
      return { response: null, error: err.message };
    }
  };

// meeting share update API call
export const updateMeetingShare =
  ({ meetingShareUuid, meetingUuid, payload }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.UPDATE_MEETING_SHARE_REQUEST
    });
    try {
      const payloadObj = decamelizeKeys(payload, '_');
      const result = await patch(
        `${env.avomaApiUrl}/api/v1/meetingshares/${meetingShareUuid}/`,
        payloadObj
      );
      const response = camelize(result);
      dispatch({
        type: MeetingActions.UPDATE_MEETING_SHARE_SUCCESS,
        meetingUuid,
        response
      });

      return response;
    } catch (err) {
      dispatch({
        type: MeetingActions.UPDATE_MEETING_SHARE_ERROR,
        error: err.message
      });

      throw err;
    }
  };

// share meeting with emails
export const shareMeetingWithEmails =
  ({ meetingUuid, publicShareUuid, payload, queryParams = '' }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.SHARE_MEETING_REQUEST,
      meta: {
        analytics: EventTypes.track
      }
    });
    try {
      const URL = `${env.avomaApiUrl}/api/v1/meetingshares/${publicShareUuid}/add_emails/${queryParams}`;
      const response = await post(URL, payload);
      dispatch({
        type: MeetingActions.SHARE_MEETING_SUCCESS,
        meetingUuid,
        share: camelize(response)
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.SHARE_MEETING_ERROR,
        error
      });
    }
  };

export const shareMeetingSuccess =
  (meetingUuid, sharedUsers, sharedTeams = []) =>
  dispatch => {
    dispatch({
      type: MeetingActions.SHARE_MEETING_LOCAL,
      meetingUuid,
      sharedUsers,
      sharedTeams
    });
  };

// remove emails from share
export const unshareMeetingWithEmails =
  ({ meetingUuid, publicShareUuid, payload }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.UNSHARE_MEETING_REQUEST,
      meta: {
        analytics: EventTypes.track
      }
    });
    try {
      const URL = `${env.avomaApiUrl}/api/v1/meetingshares/${publicShareUuid}/delete_emails/`;
      const response = await post(URL, payload);
      dispatch({
        type: MeetingActions.UNSHARE_MEETING_SUCCESS,
        meetingUuid,
        share: camelize(response)
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.UNSHARE_MEETING_ERROR,
        error
      });
    }
  };

// share meeting with teams
export const shareMeetingWithTeams =
  ({ meetingUuid, publicShareUuid, payload, queryParams = '' }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.SHARE_MEETING_REQUEST,
      meta: {
        analytics: EventTypes.track
      }
    });
    try {
      const URL = `${env.avomaApiUrl}/api/v1/meetingshares/${publicShareUuid}/add_teams/${queryParams}`;
      const response = await post(URL, payload);
      dispatch({
        type: MeetingActions.SHARE_MEETING_SUCCESS,
        meetingUuid,
        share: camelize(response)
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.SHARE_MEETING_ERROR,
        error
      });
    }
  };

// remove teams from share
export const unshareMeetingWithTeams =
  ({ meetingUuid, publicShareUuid, payload }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.UNSHARE_MEETING_REQUEST,
      meta: {
        analytics: EventTypes.track
      }
    });
    try {
      const URL = `${env.avomaApiUrl}/api/v1/meetingshares/${publicShareUuid}/delete_teams/`;
      const response = await post(URL, payload);
      dispatch({
        type: MeetingActions.UNSHARE_MEETING_SUCCESS,
        meetingUuid,
        share: camelize(response)
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.UNSHARE_MEETING_ERROR,
        error
      });
    }
  };

// Share a meeting to Slack
export const shareMeetingToSlack =
  ({ payload }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.SHARE_MEETING_TO_SLACK_REQUEST
    });
    try {
      let response = await post(
        `${env.avomaApiUrl}/api/v1/slack/sharing/meeting_share/`,
        payload
      );
      response = camelize(response);

      dispatch({
        type: MeetingActions.SHARE_MEETING_TO_SLACK_SUCCESS,
        response
      });
    } catch (err) {
      dispatch({
        type: MeetingActions.SHARE_MEETING_TO_SLACK_ERROR,
        error: err.message
      });
    }
  };

// Request a meeting to be shared with a single org member
export const requestMeetingShare =
  ({ meetingUuid, payload }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.REQUEST_MEETING_SHARE_REQUEST,
      meta: {
        analytics: EventTypes.track
      }
    });
    try {
      let response = await post(
        `${env.avomaApiUrl}/api/v1/meetingshares/request_access/${meetingUuid}/`,
        payload
      );
      response = camelize(response);

      dispatch({
        type: MeetingActions.REQUEST_MEETING_SHARE_SUCCESS,
        meetingUuid,
        response
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.REQUEST_MEETING_SHARE_ERROR,
        error
      });
    }
  };

// set the meeting in redux store
export const setMeeting = meeting => dispatch => {
  dispatch({
    type: MeetingActions.SET_MEETING,
    meeting
  });
};

// Fetch the automatically inserted template for the current meeting
export const fetchMeetingAssociatedTemplate = meetingUuid => async dispatch => {
  dispatch({
    type: MeetingActions.FETCH_MEETING_ASSOCIATED_TEMPLATE_REQUEST,
    meetingUuid
  });

  try {
    const result = await get(
      `${env.avomaApiUrl}/api/v1/meeting/${meetingUuid}/collabnotes/associated-template/`
    );

    const associatedTemplate = camelize(result[0]);
    dispatch({
      type: MeetingActions.FETCH_MEETING_ASSOCIATED_TEMPLATE_SUCCESS,
      associatedTemplate,
      meetingUuid
    });
  } catch (err) {
    dispatch({
      type: MeetingActions.FETCH_MEETING_ASSOCIATED_TEMPLATE_FAILURE,
      err,
      meetingUuid
    });
  }
};

export const fetchMeetingUploadUrl = () => async dispatch => {
  dispatch({
    type: MeetingActions.FETCH_MEETING_UPLOAD_URL_REQUEST,
    meta: {
      analytics: {
        eventType: EventTypes.track
      }
    }
  });

  try {
    const response = await get(
      `${env.avomaApiUrl}/api/v1/meeting_upload/get_signed_url/`
    );
    dispatch({
      type: MeetingActions.FETCH_MEETING_UPLOAD_URL_SUCCESS,
      response
    });
  } catch (err) {
    dispatch({
      type: MeetingActions.FETCH_MEETING_UPLOAD_URL_FAILURE,
      error: err
    });
  }
};

export const uploadMeeting =
  ({
    subject,
    s3Key,
    meetingTypeUuid,
    meetingOutcomeUuid,
    meetingUuid,
    privacySettings
  }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.UPLOAD_MEETING_REQUEST,
      meta: {
        analytics: {
          eventType: EventTypes.track
        }
      }
    });

    const mediaType = getMediaType(s3Key);

    if (!mediaType) {
      dispatch({
        type: MeetingActions.UPLOAD_MEETING_FAILURE,
        error: { wrongFormat: [L10Errors.wrongFormat] }
      });
      return null;
    }

    const payload = {
      subject,
      s3_key: s3Key,
      meeting_type_uuid: meetingTypeUuid,
      meeting_outcome_uuid: meetingOutcomeUuid,
      media: mediaType,
      ...(meetingUuid && { meeting_uuid: meetingUuid }),
      ...(privacySettings && {
        privacy_settings: {
          privacy: privacySettings.privacy,
          teams_with_access: (privacySettings.teamsWithAccess || []).map(
            team => team.uuid
          )
        }
      })
    };

    try {
      const response = await post(
        `${env.avomaApiUrl}/api/v1/meeting_upload/`,
        payload
      );
      dispatch({
        type: MeetingActions.UPLOAD_MEETING_SUCCESS,
        response
      });
    } catch (err) {
      dispatch({
        type: MeetingActions.UPLOAD_MEETING_FAILURE,
        error: err.message
      });
    }
  };

export const fetchMeetingCount = query => async dispatch => {
  dispatch({
    type: MeetingActions.FETCH_MEETING_COUNT_REQUEST
  });

  try {
    const response = await get(
      `${env.avomaApiUrl}/api/v1/es_v2/meetings/search/?${QueryString.stringify(
        query
      )}&count_only=true`
    );
    dispatch({
      type: MeetingActions.FETCH_MEETING_COUNT_SUCCESS,
      response
    });
  } catch (error) {
    dispatch({
      type: MeetingActions.FETCH_MEETING_COUNT_FAILURE,
      error
    });
  }
};

export const invokeTranscriptionRegeneration = async (
  meetingUuid,
  language
) => {
  try {
    await post(`${env.avomaApiUrl}/transcription/regenerate_transcription/`, {
      meeting_uuid: meetingUuid,
      language
    });
    return true;
  } catch (e) {
    return false;
  }
};

const pendingAttributedUserForceRefetch = new Set();

export const fetchMeetingAttributedUserDetails =
  meetingUuidOrOptions => async (dispatch, getState) => {
    const requestTs = Date.now();
    const options =
      typeof meetingUuidOrOptions === 'string'
        ? { meetingUuid: meetingUuidOrOptions, forceRefetch: false }
        : meetingUuidOrOptions || {};
    const { meetingUuid, forceRefetch = false } = options;
    if (!meetingUuid) return;

    const meteringEntity = getState()?.meetings?.metering?.[meetingUuid] || {};
    if (meteringEntity.fetching) {
      if (forceRefetch) {
        pendingAttributedUserForceRefetch.add(meetingUuid);
      }
      return;
    }

    const shouldFetch =
      forceRefetch ||
      meteringEntity.error ||
      !meteringEntity.requestTs ||
      requestTs - meteringEntity.requestTs > 300000;
    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_MEETING_ATTRIBUTED_USER_DETAILS_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const response = await get(
        `${env.avomaApiUrl}/api/v1/meetings/${meetingUuid}/attributed_user_data/`
      );

      dispatch({
        type: MeetingActions.FETCH_MEETING_ATTRIBUTED_USER_DETAILS_SUCCESS,
        response: camelize(response),
        meetingUuid,
        requestTs
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.FETCH_MEETING_ATTRIBUTED_USER_DETAILS_FAILURE,
        error,
        meetingUuid,
        requestTs
      });
    } finally {
      if (pendingAttributedUserForceRefetch.delete(meetingUuid)) {
        await fetchMeetingAttributedUserDetails({
          meetingUuid,
          forceRefetch: true
        })(dispatch, getState);
      }
    }
  };

/** @internal */
export function __resetPendingAttributedUserForceRefetchForTests() {
  pendingAttributedUserForceRefetch.clear();
}

export const fetchHighlightSummarization =
  ({ meetingUuid, signal }) =>
  async (dispatch, getState) => {
    const { startTime, endTime } = getState().app.createHighlight;
    const diffTime = endTime - startTime;

    try {
      if (diffTime < 10) {
        return {
          title: null,
          summary: null,
          durationError: true // triggered when the duration is less than 10 seconds
        };
      }

      if (
        Number.isNaN(startTime) ||
        Number.isNaN(endTime) ||
        typeof startTime !== 'number' ||
        typeof endTime !== 'number'
      ) {
        return {
          title: null,
          summary: null,
          durationError: false
        };
      }

      const url = `start_time=${Math.ceil(startTime)}&end_time=${Math.ceil(
        endTime
      )}&meeting_uuid=${meetingUuid}&milliseconds=${false}`;

      const response = await get(
        `${env.avomaApiUrl}/api/v1/highlightsummarize/?${url}`,
        { signal }
      );

      if (response.title === null || response.summary === null) {
        throw Error('[avoma] Something went wrong while summarization!');
      }

      return response;
    } catch (error) {
      return {
        title: null,
        summary: null,
        durationError: false
      };
    }
  };

// Trigger email notification when a logged-in user views a meeting
// This API call is tied to the FIRST time a user clicks PLAY on the recording
export const triggerMeetingViewedNotification =
  ({ meetingUuid }) =>
  async dispatch => {
    dispatch({
      type: MeetingActions.VIEWED_MEETING_REQUEST
    });
    try {
      const response = await post(
        `${env.avomaApiUrl}/api/v1/meetingshares/viewed_meeting/${meetingUuid}/`
      );
      dispatch({
        type: MeetingActions.VIEWED_MEETING_SUCCESS,
        response: camelize(response)
      });
    } catch (err) {
      dispatch({
        type: MeetingActions.VIEWED_MEETING_FAILURE,
        error: err.message
      });
    }
  };

export const fetchCalendarViewMeetings =
  ({ filters, type = 'allMeetings' }) =>
  async dispatch => {
    let url;

    let query;

    if (!isEmpty(filters)) {
      const encodedFilters = filters
        .split('&')
        .filter(q => q)
        .map(q => {
          const [k, v] = q.split('=');
          return [k, encodeURIComponent(v)].join('=');
        })
        .join('&');
      query = QueryString.parse(encodedFilters);
      delete query.recording_duration__isnull;
      FILTERS_PARAMS_OMITTED_FROM_CALENDAR_VIEW_MEETINGS.forEach(param => {
        delete query[param];
      });
      url = `${
        env.avomaApiUrl
      }/api/v2/meetings/all/calendar/?${QueryString.stringify(query)}`;
    }

    await getCalendarViewMeetings({
      dispatch,
      url,
      type,
      query
    });
  };

const getCalendarViewMeetings = async ({
  dispatch,
  url: getUrl,
  type,
  query
}) => {
  dispatch({
    type: MeetingActions.FETCH_CALENDAR_VIEW_MEETINGS_REQUEST,
    category: type,
    query,
    url: getUrl
  });
  try {
    const response = await get(getUrl);
    const data = {};
    const results = camelize(response.results);

    results.forEach(meeting => {
      data[meeting.uuid] = meeting;
    });

    dispatch({
      type: MeetingActions.FETCH_CALENDAR_VIEW_MEETINGS_SUCCESS,
      category: type,
      data,
      message: response.message || {}
    });
  } catch (e) {
    dispatch({
      type: MeetingActions.FETCH_CALENDAR_VIEW_MEETINGS_FAILURE,
      category: type,
      error: e.message
    });
  }
};

export const fetchCalendarViewMeetingsV3 = ({
  payload,
  type = 'allMeetings',
  component
}) => {
  const url = `${env.avomaApiUrl}/api/v3/meetings/filter_calendar/?skip_count=true`;

  return async dispatch => {
    await getCalendarViewMeetingsV3({
      dispatch,
      url,
      type,
      payload,
      component,
      method: 'POST'
    });
  };
};

const getCalendarViewMeetingsV3 = async ({
  dispatch,
  url,
  type,
  payload,
  component,
  method = 'POST'
}) => {
  dispatch({
    type: MeetingActions.FETCH_CALENDAR_VIEW_MEETINGS_REQUEST,
    category: type,
    url
  });
  try {
    const response = await post(url, {
      ...payload
    });

    const data = {};
    const results = camelize(response.results);

    results.forEach(meeting => {
      data[meeting.uuid] = meeting;
    });

    dispatch({
      type: MeetingActions.FETCH_CALENDAR_VIEW_MEETINGS_SUCCESS,
      category: type,
      data,
      message: response.message || {}
    });
  } catch (e) {
    dispatch({
      type: MeetingActions.FETCH_CALENDAR_VIEW_MEETINGS_FAILURE,
      category: type,
      error: e.message
    });

    reportMeetingFetchError({
      error: e,
      url,
      component,
      method,
      action: 'load calendar meetings'
    });
  }
};

const isStoreValueStale = ({
  getState,
  meetingUuid,
  entityType,
  forceRefetch = false
}) => {
  const requestTs = Date.now();
  const { meetings = {} } = getState();
  const shouldForceRefetch = meetings.forceRefetch;
  const meetingEntity =
    meetings?.meetingEntityFetching?.[meetingUuid]?.[entityType] || {};
  if (!meetingEntity.requestTs || shouldForceRefetch || forceRefetch)
    return true;
  const value = requestTs - meetingEntity.requestTs;

  // Data is not stale within 5 mins
  // if the query is same and above condition avoid calls
  return value > 300000;
};

export const fetchMeetingCrmAssociations =
  ({ meetingUuid, forceRefetch = false }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();
    const shouldFetch = isStoreValueStale({
      getState,
      meetingUuid,
      entityType: 'crmAssociations',
      forceRefetch
    });

    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_MEETING_CRM_ASSOCIATIONS_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const response = await get(
        `${env.avomaApiUrl}/api/v2/meetings/${meetingUuid}/crm_associations/`
      );

      dispatch({
        type: MeetingActions.FETCH_MEETING_CRM_ASSOCIATIONS_SUCCESS,
        response: camelize(response),
        meetingUuid,
        requestTs
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.FETCH_MEETING_CRM_ASSOCIATIONS_FAILURE,
        error,
        meetingUuid,
        requestTs
      });
    }
  };

export const fetchMeetingCounts =
  ({ meetingUuid, forceRefetch = false }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();
    const shouldFetch = isStoreValueStale({
      getState,
      meetingUuid,
      entityType: 'counts',
      forceRefetch
    });

    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_MEETING_COUNTS_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const response = await get(
        `${env.avomaApiUrl}/api/v2/meetings/${meetingUuid}/counts/`
      );

      dispatch({
        type: MeetingActions.FETCH_MEETING_COUNTS_SUCCESS,
        response: camelize(response),
        meetingUuid,
        requestTs
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.FETCH_MEETING_COUNTS_FAILURE,
        error,
        meetingUuid,
        requestTs
      });
    }
  };

export const fetchMeetingSharesData =
  ({ meetingUuid, forceRefetch = false }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();
    const shouldFetch = isStoreValueStale({
      getState,
      meetingUuid,
      entityType: 'share',
      forceRefetch
    });

    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_MEETING_SHARES_DATA_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const response = await get(
        `${env.avomaApiUrl}/api/v2/meetings/${meetingUuid}/shares/`
      );

      dispatch({
        type: MeetingActions.FETCH_MEETING_SHARES_DATA_SUCCESS,
        response: camelize(response),
        meetingUuid,
        requestTs
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.FETCH_MEETING_SHARES_DATA_FAILURE,
        error,
        meetingUuid,
        requestTs
      });
    }
  };

export const fetchMeetingShareByUuid =
  ({ meetingUuid, meetingShareUuid, forceRefetch = false }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();
    const shouldFetch = isStoreValueStale({
      getState,
      meetingUuid,
      entityType: 'share',
      forceRefetch
    });

    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_MEETING_SHARES_DATA_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const response = await get(
        `${env.avomaApiUrl}/api/v1/meetingshares/${meetingShareUuid}/`
      );

      dispatch({
        type: MeetingActions.FETCH_MEETING_SHARES_DATA_SUCCESS,
        response: camelize(response),
        meetingUuid,
        requestTs
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.FETCH_MEETING_SHARES_DATA_FAILURE,
        error,
        meetingUuid,
        requestTs
      });
    }
  };

export const fetchMeetingPlaylistData =
  ({ meetingUuid, forceRefetch = false }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();
    const shouldFetch = isStoreValueStale({
      getState,
      meetingUuid,
      entityType: 'playlists',
      forceRefetch
    });

    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_MEETING_PLAYLIST_DATA_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const response = await get(
        `${env.avomaApiUrl}/api/v2/meetings/${meetingUuid}/playlists/`
      );

      dispatch({
        type: MeetingActions.FETCH_MEETING_PLAYLIST_DATA_SUCCESS,
        response: camelize(response),
        meetingUuid,
        requestTs
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.FETCH_MEETING_PLAYLIST_DATA_FAILURE,
        error,
        meetingUuid,
        requestTs
      });
    }
  };

export const fetchMeetingPurposeOutcomes =
  ({ meetingUuid, forceRefetch = false }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();
    const shouldFetch = isStoreValueStale({
      getState,
      meetingUuid,
      entityType: 'attributes',
      forceRefetch
    });

    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_MEETING_PURPOSE_OUTCOMES_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const response = await get(
        `${env.avomaApiUrl}/api/v2/meetings/${meetingUuid}/attributes/`
      );

      dispatch({
        type: MeetingActions.FETCH_MEETING_PURPOSE_OUTCOMES_SUCCESS,
        response: camelize(response),
        meetingUuid,
        requestTs
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.FETCH_MEETING_PURPOSE_OUTCOMES_FAILURE,
        error,
        meetingUuid,
        requestTs
      });
    }
  };

export const fetchMeetingRowRecordingData =
  ({ meetingUuid, forceRefetch = false }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();
    const shouldFetch = isStoreValueStale({
      getState,
      meetingUuid,
      entityType: 'recording',
      forceRefetch
    });

    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_MEETING_ROW_RECORDING_DATA_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const response = await get(
        `${env.avomaApiUrl}/api/v2/meetings/${meetingUuid}/recording_data/`
      );

      dispatch({
        type: MeetingActions.FETCH_MEETING_ROW_RECORDING_DATA_SUCCESS,
        response: camelize(response),
        meetingUuid,
        requestTs
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.FETCH_MEETING_ROW_RECORDING_DATA_FAILURE,
        error,
        meetingUuid,
        requestTs
      });
    }
  };

export const fetchMeetingStates =
  ({ meetingUuid, forceRefetch = false }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();
    const shouldFetch = isStoreValueStale({
      getState,
      meetingUuid,
      entityType: 'states',
      forceRefetch
    });

    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_MEETING_STATES_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const response = await get(
        `${env.avomaApiUrl}/api/v2/meetings/${meetingUuid}/meetingstate/`
      );

      dispatch({
        type: MeetingActions.FETCH_MEETING_STATES_SUCCESS,
        response: camelize(response),
        meetingUuid,
        requestTs
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.FETCH_MEETING_STATES_FAILURE,
        error,
        meetingUuid,
        requestTs
      });
    }
  };

export const fetchMeetingConferenceData =
  ({ meetingUuid, forceRefetch = false }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();
    const shouldFetch = isStoreValueStale({
      getState,
      meetingUuid,
      entityType: 'conference',
      forceRefetch
    });

    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_MEETING_CONFERENCE_DATA_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const response = await get(
        `${env.avomaApiUrl}/api/v2/meetings/${meetingUuid}/conference_data/`
      );

      dispatch({
        type: MeetingActions.FETCH_MEETING_CONFERENCE_DATA_SUCCESS,
        response: camelize(response),
        meetingUuid,
        requestTs
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.FETCH_MEETING_CONFERENCE_DATA_FAILURE,
        error,
        meetingUuid,
        requestTs
      });
    }
  };

export const fetchMeetingAdditionalDetails =
  ({ meetingUuid, forceRefetch = false }) =>
  async (dispatch, getState) => {
    const requestTs = Date.now();
    const shouldFetch = isStoreValueStale({
      getState,
      meetingUuid,
      entityType: 'links',
      forceRefetch
    });

    if (!shouldFetch) return;

    dispatch({
      type: MeetingActions.FETCH_MEETING_ADDITIONAL_DETAILS_REQUEST,
      meetingUuid,
      requestTs
    });
    try {
      const response = await get(
        `${env.avomaApiUrl}/api/v2/meetings/${meetingUuid}/additional_details/`
      );

      dispatch({
        type: MeetingActions.FETCH_MEETING_ADDITIONAL_DETAILS_SUCCESS,
        response: camelize(response),
        meetingUuid,
        requestTs
      });
    } catch (error) {
      dispatch({
        type: MeetingActions.FETCH_MEETING_ADDITIONAL_DETAILS_FAILURE,
        error,
        meetingUuid,
        requestTs
      });
    }
  };

/**
 * Force-refresh per-meeting entity endpoints used by Zen recording chrome.
 * The V2 list API only returns summary fields; meetingstate / conference_data
 * must be re-fetched after a list reload so changes made in full mode (enable
 * bot, add details) appear when Zen is reopened. Attributed-user details use
 * the same action with stale/in-flight dedupe so mounted rows refresh policy
 * data after list reloads without double-requesting on initial mount.
 */
export const refreshMinimalZenMeetingEntities =
  (meetingUuids = []) =>
  dispatch => {
    const uuids = [...new Set(meetingUuids.filter(Boolean))];
    uuids.forEach(meetingUuid => {
      dispatch(fetchMeetingStates({ meetingUuid, forceRefetch: true }));
      dispatch(fetchMeetingConferenceData({ meetingUuid, forceRefetch: true }));
      dispatch(
        fetchMeetingAttributedUserDetails({ meetingUuid, forceRefetch: true })
      );
    });
  };

// Show read-only transcript search via timestamp component in place of string match search
export const showTranscriptTimestampSearch = payload => async dispatch => {
  dispatch({
    type: MeetingActions.SHOW_TRANSCRIPT_TIMESTAMP_SEARCH,
    response: payload
  });
};

// Hide read-only transcript search via timestamp component in place of string match search
export const hideTranscriptTimestampSearch = () => async dispatch => {
  dispatch({
    type: MeetingActions.HIDE_TRANSCRIPT_TIMESTAMP_SEARCH
  });
};

export const setCalendarDate = date => async dispatch => {
  dispatch({
    type: MeetingActions.UPDATE_CALENDAR_DATE,
    data: { calendarDate: date }
  });
};

export const setCalendarWeekDate = date => async dispatch => {
  dispatch({
    type: MeetingActions.UPDATE_CALENDAR_WEEK_DATE,
    data: { calendarWeekDate: date }
  });
};

export const updateMeetingCounts =
  // entity = comments | highlights | scorecards
  // requestType = add | remove
  // count = in cases where we can't increment/decrement by 1 (for example: deleting comments with replies in them)


    ({ meetingUuid, entity, requestType, count }) =>
    async dispatch => {
      dispatch({
        type: MeetingActions.UPDATE_MEETING_COUNTS,
        meetingUuid,
        entity,
        requestType,
        count
      });
    };
